
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export const bookmarkService = {
  /**
   * Save an article to the user's bookmarks
   */
  async saveArticle(articleId: string) {
    try {
      if (!articleId) {
        throw new Error('Article ID is required');
      }

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be logged in to bookmark articles');
      }

      const { error } = await supabase
        .from('user_bookmarks')
        .insert({ 
          article_id: articleId,
          user_id: user.id
        });

      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error saving article:', error);
      toast({
        title: 'Error saving article',
        description: 'There was a problem saving this article.',
        variant: 'destructive'
      });
      return { success: false, error };
    }
  },

  /**
   * Remove an article from the user's bookmarks
   */
  async unsaveArticle(articleId: string) {
    try {
      if (!articleId) {
        throw new Error('Article ID is required');
      }

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be logged in to remove bookmarks');
      }

      const { error } = await supabase
        .from('user_bookmarks')
        .delete()
        .eq('article_id', articleId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error removing article bookmark:', error);
      toast({
        title: 'Error removing bookmark',
        description: 'There was a problem removing this bookmark.',
        variant: 'destructive'
      });
      return { success: false, error };
    }
  },

  /**
   * Check if an article is bookmarked by the current user
   */
  async isArticleBookmarked(articleId: string) {
    try {
      if (!articleId) {
        return false;
      }

      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return false;
      }

      const { data, error } = await supabase
        .from('user_bookmarks')
        .select('id')
        .eq('article_id', articleId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      
      return Boolean(data);
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  },

  /**
   * Get all bookmarked articles for the current user
   */
  async getUserBookmarks() {
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { data: [], error: new Error('User must be logged in to fetch bookmarks') };
      }

      // First query: Get bookmarks with article data using the correct foreign key relationship
      const { data, error } = await supabase
        .from('user_bookmarks')
        .select(`
          id,
          created_at,
          article_id,
          articles!article_id(
            id,
            title,
            lead,
            featured_image_url,
            header_image_url,
            published_at,
            reading_time,
            author_id
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      if (!data || data.length === 0) {
        return { data: [], error: null };
      }
      
      // Extract all author IDs from the articles
      const authorIds = data
        .map(bookmark => bookmark.articles?.author_id)
        .filter(id => id !== null && id !== undefined);
      
      // Second query: Get author information
      let authorData: any[] = [];
      if (authorIds.length > 0) {
        const { data: authors, error: authorError } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .in('id', [...new Set(authorIds)]); // Use Set to remove duplicates
        
        if (authorError) throw authorError;
        authorData = authors || [];
      }
      
      // Transform the data to a more usable format and join with author data
      const bookmarks = data.map(bookmark => {
        const author = authorData.find(a => a.id === bookmark.articles.author_id) || {
          id: bookmark.articles.author_id,
          display_name: 'Unknown Author',
          avatar_url: null
        };
        
        return {
          id: bookmark.id,
          savedAt: bookmark.created_at,
          article: {
            id: bookmark.articles.id,
            title: bookmark.articles.title,
            lead: bookmark.articles.lead,
            image: bookmark.articles.featured_image_url || bookmark.articles.header_image_url,
            publishedAt: bookmark.articles.published_at,
            readTime: bookmark.articles.reading_time,
            author: {
              id: author.id,
              name: author.display_name,
              avatar: author.avatar_url
            }
          }
        };
      });
      
      return { data: bookmarks, error: null };
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      return { data: [], error };
    }
  }
};
